import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Image, Modal, Spin, Switch } from 'antd'
import isEmpty from 'lodash/isEmpty'
import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { StyledSpinContainer } from '../styled'

import { useClientTerms } from './queries'
import { RichTextEditor } from './RichTextEditor'
import SignUpLanguageImage from './SignUpLanguageImage.png'
import { COPY_TYPES, getCopyData, getCheckboxData, getHtmlContentTextLength, stripPrefix } from './utils'

import { patchClientSettings } from 'api/clients'
import { getSettings } from 'api/signUp'
import { QUERIES } from 'constants/queries'
import { useToastMessage } from 'hooks/useNotification'

const StyledTextAndPictureRow = styled.div`
  display: flex;
  margin-top: 14px;
`

const StyledTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
`

const StyledHeader = styled.h3`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 22px;
  line-height: 126%;
`

const regularText = css`
  font-family: Avenir;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0.08px;
  color: #333333;
`
const StyledSubHeader = styled.h5`
  ${regularText}
  margin-bottom: 14px;
`
const StyledList = styled.ol`
  ${regularText}
  padding-left: 20px;
  text-indent: 0;
  li {
    margin-bottom: 4px;
  }
`

const StyledImageContainer = styled.div`
  display: flex;
  flex: 1;
`

const StyledInputRow = styled.div`
  margin-top: 20px;

  span {
    margin-right: 8px;
  }
`

const StyledCheckboxRow = styled.div`
  display: flex;
  margin-top: 20px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const StyledCheckboxLabel = styled.span`
  ${regularText}
  flex: 1;
`
const StyledOptionalDisclaimer = styled.p`
  ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.CAPTION2};
  font-style: italic;
  padding: 4px;
`

const CHAR_LIMIT = 1000

const isSignUpTextInvalid = (signUpText: string, isRequired: boolean): boolean => {
  if (getHtmlContentTextLength(signUpText) > CHAR_LIMIT) return true
  if (isRequired && getHtmlContentTextLength(signUpText) < 10) return true
  return false
}

const isLegalTextInvalid = (legalText: string, isRequired: boolean): boolean => {
  // We need to take into account pre-pending "(Optional)" || "(Required)" (10 chars) to the message
  if (getHtmlContentTextLength(legalText) > CHAR_LIMIT - 10) return true

  // if the message is required and the length is less than 10, return true
  if (isRequired && getHtmlContentTextLength(legalText) < 10) return true
  return false
}

type CustomCheckboxModalProps = {
  clientId: string
  onCancel(...args: unknown[]): unknown
  onDelete(...args: unknown[]): unknown
  onSave(...args: unknown[]): unknown
  isOpen: boolean
}

export const CustomCheckboxModal = ({ clientId, isOpen, onSave, onDelete, onCancel }: CustomCheckboxModalProps) => {
  const queryClient = useQueryClient()
  const { showNotification } = useToastMessage()
  const { clientTerms, saveTerms, deleteTerms, isSavingTerms } = useClientTerms(clientId)
  const { isLoading: isTermsLoading, data: terms } = clientTerms
  const { data: clientSettings, isLoading: isSettingsLoading } = useQuery(
    [QUERIES.CLIENT_SETTINGS, { id: clientId }],
    () => getSettings(clientId),
    { enabled: isOpen },
  )
  const [signUpText, setSignUpText] = useState('')
  const [legalText, setLegalText] = useState('')
  const [legalRequired, setLegalRequired] = useState(false)
  const [secondLegalText, setSecondLegalText] = useState('')
  const [secondLegalRequired, setSecondLegalRequired] = useState(false)
  const [hasCheckBox, setHasCheckBox] = useState(false)
  const [hasSecondCheckBox, setHasSecondCheckBox] = useState(false)
  const [currentDefaultTermsVisibility, setCurrentDefaultTermsVisibility] = useState<boolean | null>(null)

  const hideDefaultCommunityTermsAndConditions =
    currentDefaultTermsVisibility ?? clientSettings?.hideDefaultCommunityTermsAndConditions === true

  const { mutateAsync: patchHideTermsSetting, isLoading: isPatchingSettings } = useMutation(patchClientSettings, {
    onError() {
      showNotification('Could not update client settings. Please try again.', false)
    },
    onSuccess() {
      queryClient.invalidateQueries([QUERIES.CLIENT_SETTINGS, { id: clientId }])
    },
  })

  useEffect(() => {
    const signData = getCopyData(terms, COPY_TYPES.COPY_ONLY)
    const legalData = getCheckboxData(terms, 0)
    const secondLegalData = getCheckboxData(terms, 1)

    setSignUpText(signData.body)
    setLegalText(stripPrefix(legalData.body))
    setSecondLegalText(stripPrefix(secondLegalData.body))
    setHasCheckBox(legalData.checkedByDefault)
    setHasSecondCheckBox(secondLegalData.checkedByDefault)
    setLegalRequired(legalData.required)
    setSecondLegalRequired(secondLegalData.required)
  }, [isTermsLoading, terms])

  const handleCancel = () => {
    setCurrentDefaultTermsVisibility(null)
    onCancel()
  }

  const handleSignUpText = (content) => {
    setSignUpText(content)
  }

  const handLegalText = (content) => {
    if (/<br>/.test(content)) return setLegalText('')
    setLegalText(content)
  }

  const handleSecondLegalText = (content) => {
    if (/<br>/.test(content)) return setSecondLegalText('')
    setSecondLegalText(content)
  }

  const prepareLegalText = (text: string, required: boolean) => {
    return required
      ? text.replace(/^<p>(\(Optional\) )?/, '<p>(Required) ')
      : text.replace(/^<p>(\(Optional\) )?/, '<p>(Optional) ')
  }

  const isSaveDisabled =
    isSignUpTextInvalid(signUpText, hideDefaultCommunityTermsAndConditions) ||
    isLegalTextInvalid(legalText, legalRequired) ||
    isLegalTextInvalid(secondLegalText, secondLegalRequired) ||
    isPatchingSettings ||
    isSavingTerms

  const handleOnDelete = () => {
    deleteTerms({
      clientId,
      body: {
        terms: [
          {
            body: signUpText,
            type: COPY_TYPES.COPY_ONLY,
          },
          {
            body: prepareLegalText(legalText, legalRequired),
            type: COPY_TYPES.CHECKBOX,
            checkedByDefault: hasCheckBox,
            required: legalRequired,
          },
          {
            body: prepareLegalText(secondLegalText, secondLegalRequired),
            type: COPY_TYPES.CHECKBOX,
            checkedByDefault: hasSecondCheckBox,
            required: secondLegalRequired,
          },
        ],
      },
    })

    onDelete()
  }

  const handleOnSave = async () => {
    const filteredTerms = [
      {
        body: signUpText,
        type: COPY_TYPES.COPY_ONLY,
      },
      {
        body: prepareLegalText(legalText, legalRequired),
        type: COPY_TYPES.CHECKBOX,
        checkedByDefault: hasCheckBox,
        required: legalRequired,
      },
      {
        body: prepareLegalText(secondLegalText, secondLegalRequired),
        type: COPY_TYPES.CHECKBOX,
        checkedByDefault: hasSecondCheckBox,
        required: secondLegalRequired,
      },
    ].filter((term) => term.body !== '')

    try {
      await patchHideTermsSetting({
        id: clientId,
        body: { hide_default_community_terms_and_conditions: hideDefaultCommunityTermsAndConditions },
      })
      await saveTerms({
        clientId,
        hasTerms: !isEmpty(terms),
        body: { terms: filteredTerms },
      })
      onSave()
    } catch {
      // useMutation onError handlers already notify the user
    }
  }

  const isModalLoading = isTermsLoading || (isOpen && isSettingsLoading)

  if (isModalLoading) {
    return (
      <StyledSpinContainer>
        <Spin size="large" />
      </StyledSpinContainer>
    )
  }

  return (
    <Modal
      //Doing the empty styles to get the underline
      data-testid="legal-language-modal"
      footer={[
        <Button
          danger
          disabled={hideDefaultCommunityTermsAndConditions}
          key="delete"
          onClick={handleOnDelete}
          type="primary"
        >
          Delete
        </Button>,
        <Button disabled={isSaveDisabled} key="save" onClick={handleOnSave} type="primary">
          Save
        </Button>,
      ]}
      onCancel={handleCancel}
      open={isOpen}
      title="   "
      width={640}
    >
      <StyledTextAndPictureRow>
        <StyledTextContainer>
          <StyledHeader>Custom Sign Up Language</StyledHeader>
          <StyledSubHeader>New members will see this at sign up.</StyledSubHeader>
          <StyledList>
            <li>
              <strong>Custom Sign Up Language</strong> is placed below Community's standard text
            </li>
            <li>
              <strong>Legal Checkbox Language</strong> is placed above the "Accept" button
            </li>
            <li>
              <strong>Additional Legal Checkbox Language</strong> is placed below the first legal checkbox
            </li>
          </StyledList>
        </StyledTextContainer>
        <StyledImageContainer>
          <Image src={SignUpLanguageImage} />
        </StyledImageContainer>
      </StyledTextAndPictureRow>
      <StyledCheckboxRow>
        <StyledCheckboxLabel>Hide Community Terms & Conditions (Requires Custom Sign Up Language)</StyledCheckboxLabel>
        <Switch
          checked={hideDefaultCommunityTermsAndConditions}
          // @ts-expect-error TS(2322): Type ' checked: boolean; name: string; onClick: (... Remove this comment to see the full error message
          name="Hide Community Terms & Conditions"
          onClick={() => setCurrentDefaultTermsVisibility(!hideDefaultCommunityTermsAndConditions)}
        />
      </StyledCheckboxRow>
      <StyledInputRow>
        <p>1. Custom Sign Up Language</p>
        <RichTextEditor charLimit={CHAR_LIMIT} id="signup-tool-bar" onChange={handleSignUpText} value={signUpText} />
      </StyledInputRow>
      <StyledInputRow>
        <p>2. Legal Checkbox Language </p>
        <p>
          <span>Require members to select this checkbox</span>
          <Switch
            checked={legalRequired}
            // @ts-expect-error TS(2322): Type ' checked: boolean; name: string; onClick: (... Remove this comment to see the full error message
            name="Require members to select this checkbox"
            onClick={() => setLegalRequired(!legalRequired)}
          />
        </p>
        <RichTextEditor
          charLimit={CHAR_LIMIT}
          id="legal-tool-bar"
          key={`legal-text-editor-${legalRequired}`}
          onChange={handLegalText}
          placeholder={legalRequired ? '(Required)' : '(Optional)'}
          value={legalText}
        />
        <StyledOptionalDisclaimer>
          {legalRequired ? '(Required)' : '(Optional)'} will be added to the beginning of this message
        </StyledOptionalDisclaimer>
      </StyledInputRow>
      <StyledInputRow>
        <p>3. Additional Legal Checkbox Language </p>
        <p>
          <span>Require members to select this checkbox</span>
          <Switch
            checked={secondLegalRequired}
            // @ts-expect-error TS(2322): Type ' checked: boolean; name: string; onClick: (... Remove this comment to see the full error message
            name="Require members to select this checkbox"
            onClick={() => setSecondLegalRequired(!secondLegalRequired)}
          />
        </p>
        <RichTextEditor
          charLimit={CHAR_LIMIT}
          id="second-legal-tool-bar"
          key={`second-legal-text-editor-${secondLegalRequired}`}
          onChange={handleSecondLegalText}
          placeholder={secondLegalRequired ? '(Required)' : '(Optional)'}
          value={secondLegalText}
        />
        <StyledOptionalDisclaimer>
          {secondLegalRequired ? '(Required)' : '(Optional)'} will be added to the beginning of this message
        </StyledOptionalDisclaimer>
      </StyledInputRow>
      <StyledInputRow>
        <span>Pre-select checkbox(es) in US</span>
        <Switch
          checked={hasCheckBox}
          // @ts-expect-error TS(2322): Type ' checked: boolean; name: string; onClick: (... Remove this comment to see the full error message
          name="Pre-select checkbox in US"
          onClick={() => {
            setHasCheckBox(!hasCheckBox)
            setHasSecondCheckBox(!hasCheckBox)
          }}
        />
      </StyledInputRow>
    </Modal>
  )
}
